import { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAppStore } from "./store";
import { useThemeStore } from "./store/theme";
import React, { Suspense } from "react";
const Login = React.lazy(() => import("./pages/Login/index"));
const SignUp = React.lazy(() => import("./pages/SignUp/index"));
const SignUpSuccess = React.lazy(() => import("./pages/SignUp/Success"));
const ForgotPassword = React.lazy(() => import("./pages/Auth/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/Auth/ResetPassword"));
const AuthAction = React.lazy(() => import("./pages/Auth/AuthAction"));
const ZernioCallback = React.lazy(() => import("./pages/Settings/ZernioCallback"));
const ZernioAuth = React.lazy(() => import("./pages/Settings/ZernioAuth"));
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import MobileLayout from "./components/MobileLayout";
import MobileAppDetector from "./components/MobileAppDetector";
import { DEVELOPMENT } from "./config/config";
import { getLocalData, saveLocalData } from "./api";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { BlockedUserOverlay } from "./components/BlockedUserOverlay";

import "./App.css";
import ComingSoon from "./pages/ComingSoon";

function App() {
  const { theme } = useThemeStore();
  const { isLoggedIn, checkAuthStatus, initializeMockUser, user, setUser } =
    useAppStore();
  const navigate = useNavigate();

  const initSession = async () => {
    if (!user) {
      console.error("Cannot initialize session: user is null.");
      return;
    }
    const storage = await getLocalData("idearify-app-storage");
    await saveLocalData("idearify-app-storage", { ...storage, state: { ...(storage?.state || {}), user } });
    setUser(user);
    return user;
  };

  useEffect(() => {
    // Apply theme to document
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
  }, [theme]);

  useEffect(() => {
    // Check authentication status on app load
    const hasValidSession = checkAuthStatus(initSession);

    // If user has valid session and is on auth pages, redirect to home
    if (
      hasValidSession &&
      window.location.pathname.match(
        /^\/(login|signup|forgot-password|reset-password)/,
      )
    ) {
      // Use setTimeout to ensure navigation happens after initial render
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 0);
    }

    // Initialize mock user in development
    if (process.env.NODE_ENV === "development" && DEVELOPMENT) {
      initializeMockUser();
    }
  }, [checkAuthStatus, navigate, initializeMockUser]);

  return (
    <MobileAppDetector
      autoRedirect={false} // Set to true if you want automatic redirection
      showBanner={true}
      bannerDelay={3000}
    >
      <Helmet>
        <title>Idearify - Share Your Ideas</title>
        <meta
          name="description"
          content="Share and discover innovative ideas with the Idearify community"
        />
        <meta
          name="theme-color"
          content={theme === "dark" ? "#1A1A1A" : "#F8F8FF"}
        />
        {/* Mobile app meta tags */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="application-name" content="Idearify" />
      </Helmet>

      <UpdatePrompt />
      <BlockedUserOverlay />

      <div className={`app ${theme}`}>
        <Suspense fallback={<div className="flex h-screen w-full items-center justify-center bg-[#F8F8FF] dark:bg-[#1A1A1A]"><div className="animate-pulse flex flex-col items-center"><div className="w-12 h-12 rounded-full border-4 border-t-blue-500 border-gray-200 animate-spin mb-4"></div><div className="text-gray-500">Loading...</div></div></div>}>
          <Routes>
            {/* Public routes */}
            <Route path="/mobile-layout" element={<MobileLayout />} />
            <Route
              path="/login"
              element={!isLoggedIn ? <ComingSoon /> : <Navigate to="/" replace />}
            />
            {/* originally login */}
            {/* <Route
              path="/signup"
              element={!isLoggedIn ? <SignUp /> : <Navigate to="/" replace />}
            />
            <Route path="/signup/success" element={<SignUpSuccess />} />
            <Route
              path="/forgot-password"
              element={
                !isLoggedIn ? <ForgotPassword /> : <Navigate to="/" replace />
              }
            />
            <Route
              path="/reset-password"
              element={
                !isLoggedIn ? <ResetPassword /> : <Navigate to="/" replace />
              }
            />
            <Route path="/auth/action" element={<AuthAction />} />
            <Route path="/social/auth" element={<ZernioAuth />} />
            <Route path="/social/callback" element={<ZernioCallback />} /> */}

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </div>
    </MobileAppDetector>
  );
}

export default App;
